// Unpublished Work © 2023-2024 Deere & Company.

import { AxiosResponse } from "axios";
import { useContext } from "react";
import {
  AllOrdersRequestBody,
  Order,
  OrderCardMetricsRequest,
  OrderCardMetricsResponse,
  OrderLineItemStatusCountResponse,
  OrderLineRequestBody,
  OrderLineStatusCountRequest,
  OrderProcessRequest,
  OrdersByLineItemResponse,
  SearchOrderResponse,
} from "./orderInterfaces";
import { OrderCreateRequest } from "./types";
import { ApiContext } from "../../context/ApiContext/ApiContext";
import {
  BackOrdersDetailsResponse,
  OrderCancelResponse,
  OrderCreationResponse,
  OrderLineCancelBody,
  OrderLineRouteToDealerBody,
  OrderReviewResponse2,
  OrderUpdateResponse,
  PendingOrder,
  PendingOrdersGroup,
} from "../../pages/orders/types";
import { BlobResponse } from "../../utils/downloadFile";

const EXCEL_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const useOrderApi = () => {
  const axios = useContext(ApiContext);
  return {
    create: async (
      url: string,
      payload: OrderCreateRequest,
      lang: string
    ): Promise<OrderCreationResponse> => {
      const response = await axios.postWithRetries(
        `${url}/v1/orders/process`,
        payload,
        {
          params: { userAction: "create", lang, client: "UI" },
        }
      );
      return response.data;
    },

    cancelOrder: async (
      url: string,
      payload: OrderCreateRequest,
      lang: string
    ): Promise<OrderCancelResponse> => {
      const response = await axios.postWithRetries(
        `${url}/v1/orders/process`,
        payload,
        {
          params: { userAction: "cancel", lang, client: "UI" },
        }
      );
      return response.data;
    },

    update: async (
      url: string,
      payload: OrderProcessRequest,
      lang: string
    ): Promise<OrderUpdateResponse> => {
      const response = await axios.postWithRetries(
        `${url}/v1/orders/process`,
        payload,
        {
          params: { userAction: "update", lang, client: "UI" },
        }
      );
      return response.data;
    },

    submit: async (
      url: string,
      payload: OrderProcessRequest,
      lang: string
    ): Promise<OrderUpdateResponse> => {
      const response = await axios.postWithRetries(
        `${url}/v1/orders/process`,
        payload,
        {
          params: { userAction: "submit", lang, client: "UI" },
        }
      );
      return response.data;
    },
    review: async (
      url: string,
      payload: OrderProcessRequest,
      lang: string
    ): Promise<OrderReviewResponse2> => {
      const response = await axios.postWithRetries(
        `${url}/v1/orders/process`,
        payload,
        {
          params: { userAction: "review", lang, client: "UI" },
        }
      );
      return response.data;
    },
    summary: async (
      url: string,
      orderNumber: string,
      accountId: string,
      lang?: string
    ): Promise<Order> => {
      const response = await axios.getWithRetries<Order>(
        `${url}/v1/orders/summary/${orderNumber}?accountId=${accountId}`,
        {
          ...(lang && { params: { lang } }),
        }
      );
      return response.data;
    },
    lineReview: async (
      url: string,
      payload: OrderProcessRequest,
      lang: string
    ): Promise<OrderReviewResponse2> => {
      const response = await axios.postWithRetries(
        `${url}/v1/orders/process`,
        payload,
        {
          params: { userAction: "line_review", lang, client: "UI" },
        }
      );
      return response.data;
    },
    processOrder: async (
      orderServiceUrl: string,
      processOrderRequest: OrderProcessRequest,
      userAction: string,
      lang?: string
    ): Promise<Order> => {
      const orderResponse = await axios
        .postWithRetries(
          `${orderServiceUrl}/v1/orders/process`,
          {
            ...processOrderRequest,
          },
          {
            params: { userAction, lang, client: "UI" },
          }
        )
        .then((response: any) => response.data);

      return { ...orderResponse } as Order;
    },
    cancel: async (
      orderServiceUrl: string,
      orderLineCancel: OrderLineCancelBody
    ): Promise<any> => {
      const response = await axios.postWithRetries(
        `${orderServiceUrl}/v1/orders/lines/cancel`,
        orderLineCancel
      );
      return response.data;
    },
    routeToDealer: async (
      orderServiceUrl: string,
      orderLineRouteToDealer: OrderLineRouteToDealerBody
    ): Promise<any> => {
      const response = await axios.postWithRetries(
        `${orderServiceUrl}/v1/orders/lines/route-to-dealer`,
        orderLineRouteToDealer
      );
      return response.data;
    },
    searchOrders: async (
      orderServiceUrl: string,
      body: AllOrdersRequestBody,
      signal?: AbortSignal
    ): Promise<SearchOrderResponse> => {
      const response = await axios.postWithRetries(
        `${orderServiceUrl}/v1/orders/search`,
        {
          ...body,
          orderCreationDateFrom: formatDate(body?.orderCreationDateFrom),
          orderCreationDateTo: formatDate(body?.orderCreationDateTo),
        },
        signal ? { signal } : undefined
      );
      return response.data;
    },

    orderLines: (url: string, body: OrderLineRequestBody) =>
      axios
        .postWithRetries<OrdersByLineItemResponse>(
          `${url}/v1/orders/lines/search`,
          body
        )
        .then((res) => res.data),
    orderLinesV2: (
      url: string,
      body: OrderLineRequestBody,
      signal?: AbortSignal
    ) =>
      axios
        .postWithRetries<OrdersByLineItemResponse>(
          `${url}/v2/orders/lines/search`,
          body,
          signal ? { signal } : undefined
        )
        .then((res) => res.data),
    getOrderLinesStatusCountV2: (
      url: string,
      body: OrderLineStatusCountRequest,
      signal?: AbortSignal
    ) =>
      axios
        .postWithRetries<OrderLineItemStatusCountResponse>(
          `${url}/v2/orders/lines/search/count`,
          body,
          signal ? { signal } : undefined
        )
        .then((res) => res.data),
    getBackOrdersDetails: (
      url: string,
      partNumber: string,
      accountId: string,
      salesOrgId: string
    ): Promise<BackOrdersDetailsResponse> =>
      axios
        .getWithRetries(
          `${url}/v1/orders/back-orders-details/${partNumber}?accountId=${accountId}&salesOrgId=${salesOrgId}`
        )
        .then((response: any) => response.data),
    downloadOrders: (
      url: string,
      accountIds: string[],
      locale: string,
      hidePromotionColumn: boolean = false,
      hideDateTimeSubmittedColumn: boolean = false,
      showDateTimeCreatedColumn: boolean = false
    ): Promise<AxiosResponse<BlobResponse>> => {
      const params = [
        ["accountIds", accountIds],
        ["locale", locale],
        ["hidePromotionColumn", hidePromotionColumn],
        ["hideDateTimeSubmittedColumn", hideDateTimeSubmittedColumn],
        ["showDateTimeCreatedColumn", showDateTimeCreatedColumn],
      ]
        .map(([key, value]) => `${key}=${value}`)
        .join("&");

      return axios.getWithRetries<BlobResponse>(
        `${url}/v1/orders/download?${params}`,
        {
          responseType: "blob",
        }
      );
    },
    downloadOrderLineItems: (
      url: string,
      accountIds: string[],
      locale: string
    ): Promise<AxiosResponse<BlobResponse>> =>
      axios.getWithRetries<BlobResponse>(
        `${url}/v1/orders/lines/download?accountIds=${accountIds}&locale=${locale}`,
        {
          responseType: "blob",
        }
      ),
    downloadPendingOrders: (
      url: string,
      accountId: string,
      pendingOrdersGroup: PendingOrdersGroup,
      locale: string
    ): Promise<AxiosResponse<BlobResponse>> => {
      console.log("download pending orders");
      return axios.getWithRetries<BlobResponse>(
        `${url}/v1/orders/pending/${pendingOrdersGroup}/${accountId}/download`,
        {
          responseType: "blob",
          params: { locale },
          headers: {
            Accept: EXCEL_MIME_TYPE,
          },
        }
      );
    },

    getPendingOrders: (
      url: string,
      accountId: string,
      pendingOrdersGroup: string
    ): Promise<PendingOrder[]> =>
      axios
        .getWithRetries<PendingOrder[]>(
          `${url}/v1/orders/pending/${pendingOrdersGroup}/${accountId}`
        )
        .then((res) => res.data),
    getOrderCardMetrics: async (
      url: string,
      body: OrderCardMetricsRequest
    ): Promise<OrderCardMetricsResponse> => {
      const response = await axios.postWithRetries(
        `${url}/v1/orders/card-metrics`,
        body
      );
      return response.data;
    },
  };
};
const formatDate = (date: Date | undefined): string | undefined =>
  date ? date.toISOString().split("T")[0] : date;

export default useOrderApi;
